import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import webrtc from '@/services/webrtc';
import WebRTCManager from '@/components/WebRTCManager';
import {
  Phone,
  PhoneOff,
  Mic,
  MicOff,
  Pause,
  Play,
  Hash,
  PhoneForwarded,
  Music,
  SkipForward,
  MessageSquare,
  Volume2,
  Users,
  Square,
  X,
  User,
  Mail,
  MapPin,
  Building,
  Calendar,
  CreditCard,
  GraduationCap,
  Loader2
} from 'lucide-react';

const CAMPAIGN_DONE_STATUSES = new Set(['answered']);

const CallPanel = ({ isActive = true }) => {
  const { user } = useAuth();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isCallActive, setIsCallActive] = useState(false);
  const [callStatus, setCallStatus] = useState('idle');
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [binaType, setBinaType] = useState('open');
  const [currentBina, setCurrentBina] = useState('');
  const [dtmfKeys, setDtmfKeys] = useState('');
  const [contacts, setContacts] = useState([]);
  const [autoDialMode, setAutoDialMode] = useState(false);
  const [currentContactIndex, setCurrentContactIndex] = useState(0);
  const [selectedContactIndex, setSelectedContactIndex] = useState(null); // null = no contact selected
  const [currentCallId, setCurrentCallId] = useState(null);
  const [currentContact, setCurrentContact] = useState(null);
  const [useRealWebRTC, setUseRealWebRTC] = useState(true);
  const [callInfo, setCallInfo] = useState(null);
  const [callDirection, setCallDirection] = useState('outbound');
  const [webrtcRegistered, setWebrtcRegistered] = useState(false);
  const [contactLists, setContactLists] = useState([]);
  const [selectedList, setSelectedList] = useState('');
  const [clientDTMF, setClientDTMF] = useState('');
  const [audioFiles, setAudioFiles] = useState([]);
  const [selectedAudio, setSelectedAudio] = useState('');
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [transferNumber, setTransferNumber] = useState('');
  const [smsMessage, setSmsMessage] = useState('');
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [callerId, setCallerId] = useState('');
  const [savingCallerId, setSavingCallerId] = useState(false);
  const [loadingCallerId, setLoadingCallerId] = useState(false);

  const dtmfRef = useRef('');
  const timerRef = useRef(null);
  const callInfoRef = useRef(null);
  const callDurationRef = useRef(0);
  const countdownRef = useRef(null);
  const autoDialIntervalRef = useRef(null);
  const shouldStartAutoDialRef = useRef(false);
  const currentContactIndexRef = useRef(0);
  const autoDialModeRef = useRef(false);
  const contactsRef = useRef([]);
  const isCallActiveRef = useRef(false);
  const selectedListRef = useRef('');
  const campaignKickoffRef = useRef(false);
  const continueCampaignRef = useRef(() => {});
  const campaignAdvanceTokenRef = useRef(0);
  const dtmfPad = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];
  useEffect(() => {
    if (user?.exten) {
      fetchContacts();
      fetchContactLists();
      fetchAudioFiles();
      setupWebRTCCallbacks();
      restoreActiveCall();
      webrtc.onStopCampaign = () => {
        campaignAdvanceTokenRef.current += 1;
        setAutoDialMode(false);
        setShowCountdown(false);
        setCountdown(3);
        if (countdownRef.current) {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
        }
        if (autoDialIntervalRef.current) {
          clearInterval(autoDialIntervalRef.current);
          autoDialIntervalRef.current = null;
        }
        loadCampaignQueue({ resetIndex: true });
      };
    }
  }, [user?.exten]);

  useEffect(() => {
    if (selectedList) {
      loadCampaignQueue({ resetIndex: true });
    }
  }, [selectedList]);

  useEffect(() => {
    if (!isActive) return;
    if (!webrtc.getStatus().hasActiveCall) {
      setIsCallActive(false);
    }
    fetchContactLists();
    if (!autoDialModeRef.current) {
      loadCampaignQueue({ resetIndex: true });
    }
  }, [isActive]);

  // Keep refs in sync with state
  useEffect(() => {
    autoDialModeRef.current = autoDialMode;
    webrtc.setCampaignActive(autoDialMode);
  }, [autoDialMode]);

  useEffect(() => {
    contactsRef.current = contacts;
  }, [contacts]);

  useEffect(() => {
    selectedListRef.current = selectedList;
  }, [selectedList]);

  useEffect(() => {
    isCallActiveRef.current = isCallActive;
  }, [isCallActive]);

  // Stop countdown/timers when auto-dial is turned off. Kickoff happens in prepareAndStartCampaign.
  useEffect(() => {
    if (!autoDialMode) {
      campaignKickoffRef.current = false;
      setShowCountdown(false);
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      if (autoDialIntervalRef.current) {
        clearInterval(autoDialIntervalRef.current);
        autoDialIntervalRef.current = null;
      }
    }
  }, [autoDialMode]);

  // Handle countdown
  useEffect(() => {
    if (!showCountdown || countdown <= 0) return;

    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          // Countdown finished
          if (countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, [showCountdown, countdown]);

  // Start auto-dial when countdown reaches 0
  useEffect(() => {
    if (countdown === 0 && showCountdown && autoDialMode && !isCallActive && contacts.length > 0) {
      if (webrtc.getStatus().hasIncomingCall) {
        setShowCountdown(false);
        return;
      }
      setShowCountdown(false);
      const index = currentContactIndexRef.current;
      if (index < contacts.length) {
        setCurrentContactIndex(index);
        // Use setTimeout to ensure state is updated before calling
        setTimeout(() => {
          if (autoDialModeRef.current && !isCallActiveRef.current && !webrtc.getStatus().hasIncomingCall) {
            handleCall();
          }
        }, 100);
      }
    }
  }, [countdown, showCountdown, autoDialMode, isCallActive, contacts.length]);

  useEffect(() => {
    if (callStatus === 'connected') {
      timerRef.current = setInterval(() => {
        setCallDuration((prev) => {
          const newDuration = prev + 1;
          callDurationRef.current = newDuration; // Update ref with current duration
          return newDuration;
        });
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [callStatus]);

  const setupWebRTCCallbacks = () => {
    webrtc.onCallStarted = (info = {}) => {
      setCallStatus('calling');
      setIsCallActive(true);
      if (info.direction === 'inbound') {
        setCallDirection('inbound');
        if (info.phone) {
          setPhoneNumber(info.phone);
        }
        const inboundInfo = {
          phone: info.phone || '',
          contact_id: null,
          contact_name: info.displayName || info.phone || 'Chamada recebida',
          bina: user?.exten,
          direction: 'inbound',
        };
        setCallInfo(inboundInfo);
        callInfoRef.current = inboundInfo;
        toast.info(`Chamada recebida de ${inboundInfo.contact_name}`);
        return;
      }
      setCallDirection('outbound');
      toast.info('Chamando...');
    };

    webrtc.onCallConnected = () => {
      setCallStatus('connected');
      setCallDuration(0);
      callDurationRef.current = 0; // Reset ref when call connects
      toast.success('Chamada conectada!');
    };

    webrtc.onCallEnded = async ({ cause, duration: webrtcDuration }) => {
      const actualDuration = callDurationRef.current > 0 ? callDurationRef.current : webrtcDuration;
      const finalStatus = actualDuration < 5 ? 'not_answered' : 'answered';
      const currentIndexBeforeReset = currentContactIndexRef.current;

      try {
        await saveCallToHistory(finalStatus, actualDuration);
      } catch (error) {
        console.error('Error saving call history after hangup:', error);
      }
      resetCall();
      toast.info(`Chamada encerrada: ${cause}`);
      continueCampaignRef.current(currentIndexBeforeReset);
    };

    webrtc.onCallFailed = async (cause) => {
      const currentIndexBeforeReset = currentContactIndexRef.current;
      try {
        await saveCallToHistory('not_answered', 0);
      } catch (error) {
        console.error('Error saving call history after failure:', error);
      }
      resetCall();
      toast.error(`Chamada falhou: ${cause}`);
      continueCampaignRef.current(currentIndexBeforeReset);
    };

    webrtc.onDTMFSent = (digit) => {
      setDtmfKeys((prev) => prev + digit);
    };

    webrtc.onDTMFReceived = (digit) => {
      console.log('🎯 CallPanel: DTMF recebido do cliente:', digit);
      console.log('🎯 CallPanel: clientDTMF antes:', clientDTMF);
       dtmfRef.current += digit;   // instant update
       setDtmfKeys(dtmfRef.current); // UI sync
      setClientDTMF((prev) => {
        const newValue = prev + digit;
        console.log('🎯 CallPanel: clientDTMF depois:', newValue);
        return newValue;
      });
    };
    
    // Test callback is set
    console.log('✅ CallPanel: onDTMFReceived callback configurado:', !!webrtc.onDTMFReceived);
  };

  const restoreActiveCall = () => {
    const status = webrtc.getStatus();
    if (!status.hasActiveCall) {
      return;
    }
    setIsCallActive(true);
    setCallStatus(status.isEstablished ? 'connected' : 'calling');
    setIsMuted(!!status.isMuted);
    setIsOnHold(!!status.isOnHold);
    setCallDirection(status.direction === 'incoming' ? 'inbound' : 'outbound');
    if (status.remoteNumber) {
      setPhoneNumber(status.remoteNumber);
    }
    if (!callInfoRef.current && status.remoteNumber) {
      const restored = {
        phone: status.remoteNumber,
        contact_id: null,
        contact_name: status.remoteDisplayName || status.remoteNumber,
        bina: user?.exten,
        direction: status.direction === 'incoming' ? 'inbound' : 'outbound',
      };
      setCallInfo(restored);
      callInfoRef.current = restored;
    }
  };

  const loadCampaignQueue = async ({ resetIndex = false } = {}) => {
    const listId = selectedListRef.current || selectedList;
    if (!user?.exten || !listId) {
      setContacts([]);
      return [];
    }
    try {
      const response = await axios.get(`${API}/contacts`, {
        params: { list_id: listId, bina: user.exten },
      });
      const queue = (response.data || []).filter(
        (contact) => !CAMPAIGN_DONE_STATUSES.has(contact.status)
      );
      setContacts(queue);
      if (resetIndex) {
        currentContactIndexRef.current = 0;
        setCurrentContactIndex(0);
        setSelectedContactIndex(null);
      } else if (currentContactIndexRef.current >= queue.length) {
        currentContactIndexRef.current = 0;
        setCurrentContactIndex(0);
      }
      return queue;
    } catch (error) {
      console.error('Error fetching campaign contacts:', error);
      return [];
    }
  };

  const fetchContacts = async () => {
    if (selectedListRef.current || selectedList) {
      await loadCampaignQueue({ resetIndex: true });
      return;
    }
    if (!user?.exten) return;
    try {
      const response = await axios.get(`${API}/contacts`, {
        params: { bina: user.exten },
      });
      const queue = (response.data || []).filter(
        (contact) => !CAMPAIGN_DONE_STATUSES.has(contact.status)
      );
      setContacts(queue);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const fetchContactsByList = async () => {
    await loadCampaignQueue({ resetIndex: true });
  };

  const fetchContactLists = async () => {
    if (!user?.exten) return;
    try {
      const response = await axios.get(`${API}/contact-lists`, {
        params: { bina: user.exten },
      });
      const lists = response.data;
      
      // Fetch actual contact counts for each list
      const listsWithCounts = await Promise.all(
        lists.map(async (list) => {
          try {
            const contactsResponse = await axios.get(`${API}/contacts`, {
              params: { list_id: list.id, bina: user.exten },
            });
            const retryable = (contactsResponse.data || []).filter(
              (contact) => !CAMPAIGN_DONE_STATUSES.has(contact.status)
            ).length;
            return { ...list, total_contacts: retryable };
          } catch (error) {
            console.error(`Error fetching count for list ${list.id}:`, error);
            return list;
          }
        })
      );
      
      setContactLists(listsWithCounts);
      if (listsWithCounts.length > 0 && !selectedList) {
        setSelectedList(listsWithCounts[0].id);
      }
    } catch (error) {
      console.error('Error fetching lists:', error);
    }
  };

  const fetchAudioFiles = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/audio-files`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAudioFiles(response.data);
    } catch (error) {
      console.error('Error fetching audio files:', error);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleKeypadClick = (digit) => {
    setPhoneNumber(prev => prev + digit);
  };

  const handleClearNumber = () => {
    setPhoneNumber('');
  };

  const handleBackspace = () => {
    setPhoneNumber(prev => prev.slice(0, -1));
  };

  const prepareAndStartCampaign = async () => {
    if (isCallActiveRef.current || webrtc.getStatus().hasActiveCall) {
      toast.error('Encerre a chamada atual antes de iniciar a campanha');
      return;
    }
    if (webrtc.getStatus().hasIncomingCall) {
      toast.error('Atenda ou recuse a chamada recebida antes de iniciar a campanha');
      return;
    }
    if (!selectedListRef.current) {
      toast.error('Selecione uma agenda');
      return;
    }

    campaignAdvanceTokenRef.current += 1;
    const queue = await loadCampaignQueue({ resetIndex: true });
    if (!queue.length) {
      toast.info('Não há números pendentes nesta agenda');
      setAutoDialMode(false);
      return;
    }

    campaignKickoffRef.current = true;
    setAutoDialMode(true);
    setShowCountdown(true);
    setCountdown(3);
  };

  const startAutoDial = () => {
    if (!autoDialMode || contacts.length === 0 || isCallActive) return;
    
    if (currentContactIndex >= contacts.length) {
      toast.info('Todos os contatos foram discados');
      setAutoDialMode(false);
      return;
    }
    
    handleCall();
  };

  const handleContactClick = (contact, index) => {
    if (autoDialMode || isCallActive) return;
    
    setSelectedContactIndex(index);
    setCurrentContact(contact);
    setPhoneNumber(contact.phone);
    setCurrentContactIndex(index);
  };

  const handleCall = async () => {
    // Use refs to get latest values (avoid closure issues)
    const isAutoDialActive = autoDialModeRef.current;
    const currentContacts = contactsRef.current;
    const currentIndex = currentContactIndexRef.current;
    
    console.log('handleCall called', { 
      phoneNumber, 
      autoDialMode: isAutoDialActive, 
      selectedContactIndex, 
      webrtcRegistered, 
      userExten: user?.exten,
      currentContactIndexRef: currentIndex,
      contactsLength: currentContacts.length
    });
    
    // Determine which contact/number to call
    let numberToCall = phoneNumber;
    let contactData = null;

    if (isAutoDialActive && currentContacts.length > 0) {
      // Auto-dial mode: use currentContactIndex from ref (always up-to-date)
      const index = currentIndex;
      
      console.log('Auto-dial: using index', index, 'from', currentContacts.length, 'contacts');
      
      if (index >= currentContacts.length) {
        toast.info('Todos os contatos foram discados');
        setAutoDialMode(false);
        return;
      }
      
      const contact = currentContacts[index];
      if (!contact) {
        console.error('Contact not found at index:', index, 'contacts:', currentContacts);
        return;
      }
      
      console.log('Calling contact:', contact.name, contact.phone, 'at index:', index);
      
      numberToCall = contact.phone;
      contactData = contact;
      setCurrentContact(contact);
      setSelectedContactIndex(index);
      // Ensure state matches ref
      setCurrentContactIndex(index);
    } else if (selectedContactIndex !== null && contacts[selectedContactIndex]) {
      // Manual mode: use selected contact
      const contact = contacts[selectedContactIndex];
      numberToCall = contact.phone;
      contactData = contact;
      setCurrentContact(contact);
    } else if (!phoneNumber) {
      toast.error('Selecione um contato ou digite um número de telefone');
      return;
    }

    if (!user?.exten) {
      toast.error('Extensão não configurada');
      return;
    }

    if (useRealWebRTC && !webrtcRegistered) {
      toast.error('WebRTC não está registrado');
      return;
    }

    console.log('About to initiate call with:', { numberToCall, contactData, exten: user?.exten });

    try {
      const response = await axios.post(`${API}/calls/initiate`, {
        phone: numberToCall,
        contact_id: contactData?.id,
        contact_name: contactData?.name || 'Desconhecido',
        bina: user.exten,
      });

      console.log('Call initiated, response:', response.data);

      setCurrentBina(user.exten);
      setCurrentCallId(response.data.call_id);
      
      const callInfoData = {
        phone: numberToCall,
        contact_id: contactData?.id || null,
        contact_name: contactData?.name || 'Desconhecido',
        bina: user.exten,
      };
      setCallInfo(callInfoData);
      callInfoRef.current = callInfoData;
      console.log('Call info stored in ref and state:', callInfoData);
      setCallDirection('outbound');

      if (useRealWebRTC) {
        const result = await webrtc.makeCall(numberToCall);
        if (!result.success) {
          throw new Error(result.error);
        }
        toast.success('🔊 Chamada WebRTC iniciada!');
      } else {
        setCallStatus('calling');
        setIsCallActive(true);
        toast.success('Chamada iniciada (mock)');
        
        setTimeout(() => {
          setCallStatus('connected');
          setCallDuration(0);
          toast.success('Chamada conectada (mock)');
        }, 2000);
      }
    } catch (error) {
      console.error('Error initiating call:', error);
      toast.error(error.message || 'Erro ao iniciar chamada');
      setIsCallActive(false);
      setCallStatus('idle');
      setCallInfo(null);
      callInfoRef.current = null;
      if (autoDialModeRef.current) {
        continueCampaignRef.current(currentIndex);
      }
    }
  };

  const continueCampaign = (indexJustAttempted) => {
    const token = ++campaignAdvanceTokenRef.current;
    const tryAdvance = (retriesLeft) => {
      if (token !== campaignAdvanceTokenRef.current) {
        return;
      }
      if (webrtc.getStatus().hasIncomingCall) {
        return;
      }
      if (!autoDialModeRef.current) {
        return;
      }
      if (isCallActiveRef.current || webrtc.getStatus().hasActiveCall) {
        if (retriesLeft > 0) {
          setTimeout(() => tryAdvance(retriesLeft - 1), 800);
        }
        return;
      }

      const nextIndex = (typeof indexJustAttempted === 'number' ? indexJustAttempted : currentContactIndexRef.current) + 1;
      const currentContacts = contactsRef.current;

      if (!currentContacts.length || nextIndex >= currentContacts.length) {
        toast.info('Todos os contatos foram discados');
        setAutoDialMode(false);
        loadCampaignQueue({ resetIndex: true });
        fetchContactLists();
        return;
      }

      currentContactIndexRef.current = nextIndex;
      setCurrentContactIndex(nextIndex);

      setTimeout(() => {
        if (token !== campaignAdvanceTokenRef.current) {
          return;
        }
        if (
          autoDialModeRef.current &&
          !isCallActiveRef.current &&
          !webrtc.getStatus().hasIncomingCall &&
          !webrtc.getStatus().hasActiveCall
        ) {
          handleCall();
        }
      }, 1500);
    };

    setTimeout(() => tryAdvance(4), 400);
  };

  continueCampaignRef.current = continueCampaign;

  const saveCallToHistory = async (status, duration) => {
    // Use ref first (always available), then state, then fallback to current state
    const storedCallInfo = callInfoRef.current || callInfo;
    
    // Try to get phone number from WebRTC session as last resort
    let phoneFromSession = null;
    try {
      const webrtcStatus = webrtc.getStatus();
      if (webrtcStatus.hasActiveCall && webrtc.currentSession) {
        // Try to extract phone number from session remote_identity
        const remoteUri = webrtc.currentSession.remote_identity?.uri?.user;
        if (remoteUri) {
          phoneFromSession = remoteUri;
          console.log('Extracted phone from WebRTC session:', phoneFromSession);
        }
      }
    } catch (e) {
      console.warn('Could not extract phone from WebRTC session:', e);
    }
    
    const phone = storedCallInfo?.phone || currentContact?.phone || phoneNumber || phoneFromSession;
    const contactId = storedCallInfo?.contact_id || currentContact?.id || null;
    const contactName = storedCallInfo?.contact_name || currentContact?.name || 'Desconhecido';
    const bina = storedCallInfo?.bina || currentBina || null;
    
    if (!phone) {
      console.error('Cannot save call history: no phone number available');
      console.error('callInfoRef.current:', callInfoRef.current);
      console.error('callInfo (state):', callInfo);
      console.error('currentContact:', currentContact);
      console.error('phoneNumber:', phoneNumber);
      console.error('phoneFromSession:', phoneFromSession);
      console.error('numberToCall at call start should have been:', storedCallInfo?.phone);
      toast.error('Não foi possível salvar histórico: número não encontrado');
      return;
    }

    const historyData = {
      contact_id: contactId,
      contact_name: contactName,
      phone: phone,
      direction: storedCallInfo?.direction || callDirection || 'outbound',
      status,
      duration,
      bina: bina,
      dtmf_keys: dtmfRef.current || null,
    };
    
    console.log('Saving call history:', historyData);
    
    try {
      // Try to save via the call_id endpoint first (if call_id exists)
      if (currentCallId) {
        await axios.post(`${API}/calls/${currentCallId}/end`, historyData);
        console.log('Call history saved successfully via call_id endpoint');
      } else {
        // Fallback: save directly to call-history endpoint
        console.warn('No call_id available, saving directly to call-history');
        await axios.post(`${API}/call-history`, historyData);
        console.log('Call history saved successfully via direct endpoint');
      }
    } catch (error) {
      console.error('Error saving call history:', error);
      console.error('Error details:', error.response?.data || error.message);
      
      // If call_id endpoint failed, try direct endpoint as fallback
      if (currentCallId && error.response?.status !== 200) {
        try {
          console.log('Retrying with direct call-history endpoint...');
          await axios.post(`${API}/call-history`, historyData);
          console.log('Call history saved successfully via fallback endpoint');
        } catch (fallbackError) {
          console.error('Fallback save also failed:', fallbackError);
          toast.error('Erro ao salvar histórico da chamada');
        }
      } else {
        toast.error('Erro ao salvar histórico da chamada');
      }
    }
  };

  const handleHangup = async () => {
    if (useRealWebRTC) {
      webrtc.hangup();
    } else {
      const finalStatus = callDuration < 5 ? 'not_answered' : 'answered';
      await saveCallToHistory(finalStatus, callDuration);
      resetCall();
      toast.info('Chamada encerrada');
      continueCampaignRef.current(currentContactIndexRef.current);
    }
  };

  const resetCall = () => {
    setIsCallActive(false);
    setCallStatus('idle');
    setCallDuration(0);
    callDurationRef.current = 0; // Reset ref as well
    setIsMuted(false);
    setIsOnHold(false);
    setDtmfKeys('');
    setClientDTMF('');
    setCurrentBina('');
    setCurrentCallId(null);
    setCallInfo(null); // Clear stored call info after saving history
    callInfoRef.current = null; // Clear ref as well
    setCallDirection('outbound');
    // Don't clear currentContact or reset index in auto-dial mode
    if (!autoDialModeRef.current) {
      setPhoneNumber('');
      setCurrentContact(null);
    }
    // DO NOT reset currentContactIndexRef here - it should persist across calls in auto-dial mode
  };

  const handleStopAutoDial = () => {
    campaignAdvanceTokenRef.current += 1;
    setAutoDialMode(false);
    setShowCountdown(false);
    setCountdown(3);
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    if (autoDialIntervalRef.current) {
      clearInterval(autoDialIntervalRef.current);
      autoDialIntervalRef.current = null;
    }
    loadCampaignQueue({ resetIndex: true });
    fetchContactLists();
    toast.info('Discagem automática interrompida');
  };

  const toggleMute = () => {
    if (useRealWebRTC) {
      if (isMuted) {
        webrtc.unmute();
      } else {
        webrtc.mute();
      }
    }
    setIsMuted(!isMuted);
    toast.info(isMuted ? 'Áudio ativado' : 'Áudio silenciado');
  };

  const toggleHold = () => {
    if (useRealWebRTC) {
      if (isOnHold) {
        webrtc.unhold();
      } else {
        webrtc.hold();
      }
    }
    setIsOnHold(!isOnHold);
    toast.info(isOnHold ? 'Chamada retomada' : 'Chamada em espera');
  };

  const handleNext = () => {
    if (isCallActive) {
      handleHangup();
    }
    if (currentContactIndex + 1 < contacts.length) {
      setCurrentContactIndex((prev) => prev + 1);
    } else {
      toast.info('Fim da lista');
    }
  };

  const handlePlayAudio = async () => {
    if (!selectedAudio) {
      toast.error('Selecione um áudio primeiro');
      return;
    }

    const audio = audioFiles.find(a => a.id === selectedAudio);
    if (!audio) {
      toast.error('Áudio não encontrado');
      return;
    }

    try {
      const response = await webrtc.playAudioFile(`/dash_uploads/${audio.filename}`);
      if (response.success) {
        setIsAudioPlaying(true);
        toast.success(`▶️ Reproduzindo: ${audio.name}`);
      } else {
        toast.error(`Erro ao reproduzir áudio: ${response.error || 'Erro desconhecido'}`);
      }
    } catch (error) {
      console.error('Erro ao reproduzir áudio:', error);
      toast.error('Erro ao reproduzir áudio');
    }
  };

  const handleStopAudio = async () => {
    if (!isAudioPlaying) return;
    
    try {
      const response = await webrtc.stopPlayback();
      if (response.success) {
        setIsAudioPlaying(false);
        toast.info('⏹️ Reprodução interrompida');
      } else {
        toast.error(`Erro ao parar áudio: ${response.error || 'Erro desconhecido'}`);
      }
    } catch (error) {
      console.error('Erro ao parar áudio:', error);
      toast.error('Erro ao parar reprodução de áudio');
    }
  };

  const handleTransfer = async () => {
    if (!transferNumber) {
      toast.error('Digite o número para transferir');
      return;
    }

    const digits = String(transferNumber).replace(/\D/g, '');
    if (digits.length === 5) {
      toast.error('Transferência para ramais (5 dígitos) não é permitida');
      return;
    }

    try {
      const result = webrtc.transfer(transferNumber);
      if (result.success) {
        toast.success(`📞 Transferindo para: ${transferNumber}`);
        setTransferNumber('');
      } else {
        toast.error(`Erro ao transferir chamada: ${result.error || 'Erro desconhecido'}`);
      }
    } catch (error) {
      console.error('Error transferring call:', error);
      toast.error('Erro ao transferir chamada');
    }
  };

  const handleSendSMS = async () => {
    if (!smsMessage) {
      toast.error('Digite a mensagem SMS');
      return;
    }
    if (!phoneNumber && !currentContact) {
      toast.error('Sem número de destino');
      return;
    }
    toast.success('📱 SMS enviado com sucesso!');
    setSmsMessage('');
  };

  const handleSaveCallerId = async () => {
    if (!callerId) {
      toast.error('Digite o ID de chamada');
      return;
    }

    setSavingCallerId(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/caller-id/update`, {
        caller_id: callerId,
        exten: user.exten
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('ID de chamada atualizado com sucesso!');
      } else {
        toast.error(response.data.message || 'Erro ao atualizar ID de chamada');
      }
    } catch (error) {
      console.error('Error updating caller ID:', error);
      toast.error(error.response?.data?.detail || 'Erro ao atualizar ID de chamada');
    } finally {
      setSavingCallerId(false);
    }
  };

  // Fetch current caller ID on component mount or when user changes
  useEffect(() => {
    const fetchCallerId = async () => {
      if (!user?.exten) return;

      setLoadingCallerId(true);
      try {
        const token = localStorage.getItem('session_token');
        const response = await axios.get(`${API}/caller-id`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.data.caller_id) {
          setCallerId(response.data.caller_id);
        }
      } catch (error) {
        console.error('Error fetching caller ID:', error);
      } finally {
        setLoadingCallerId(false);
      }
    };

    fetchCallerId();
  }, [user?.exten]);

  return (
    <div className="p-6 max-w-[1800px] mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 mb-1">Painel URA - Atendimento</h1>
          <p className="text-slate-600">Discador inteligente com controles unificados</p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* COLUNA 1: Discador e Teclado */}
        <div className="space-y-4">
          {/* WebRTC Status */}
          {user?.exten && (
            <WebRTCManager
              exten={user.exten}
              onStatusChange={(status) => setWebrtcRegistered(status.isRegistered)}
            />
          )}

          {/* ID de chamada */}
          <Card className="border-2">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <PhoneForwarded size={16} className="text-teal-600" />
                ID de chamada
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs text-slate-600">Caller ID para chamadas outbound</Label>
                <Input
                  type="text"
                  value={callerId}
                  onChange={(e) => setCallerId(e.target.value)}
                  placeholder={loadingCallerId ? 'Carregando...' : 'Ex: 1234567890'}
                  disabled={loadingCallerId}
                  className="mt-1"
                />
              </div>
              <Button
                onClick={handleSaveCallerId}
                disabled={savingCallerId}
                className="w-full bg-teal-600 hover:bg-teal-700"
                size="sm"
              >
                {savingCallerId ? (
                  <>
                    <Loader2 size={14} className="mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  'Salvar'
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Discador Principal */}
          <Card className="border-2 border-teal-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Phone size={18} className="text-teal-600" />
                Discador URA
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Display do Número */}
              <div className="bg-slate-900 rounded-lg p-4 min-h-[60px] flex items-center justify-center">
                <input
                  type="text"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="Digite ou clique no teclado"
                  className="w-full bg-transparent text-white text-2xl text-center font-mono outline-none"
                  disabled={isCallActive}
                  data-testid="phone-input"
                />
              </div>

              {/* Teclado Numérico */}
              <div className="grid grid-cols-3 gap-2">
                {dtmfPad.map((key) => (
                  <Button
                    key={key}
                    onClick={() => handleKeypadClick(key)}
                    disabled={isCallActive}
                    variant="outline"
                    className="h-14 text-xl font-bold hover:bg-teal-50 hover:border-teal-600 transition-all"
                    data-testid={`keypad-${key}`}
                  >
                    {key}
                  </Button>
                ))}
              </div>

              {/* Controles do Teclado */}
              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={handleBackspace}
                  variant="outline"
                  size="sm"
                  disabled={isCallActive}
                >
                  ← Apagar
                </Button>
                <Button
                  onClick={handleClearNumber}
                  variant="outline"
                  size="sm"
                  disabled={isCallActive}
                >
                  Limpar
                </Button>
              </div>

              {/* Botões de Chamada */}
              <div className="grid grid-cols-2 gap-2 pt-2">
                {!isCallActive ? (
                  <Button
                    onClick={handleCall}
                    className="bg-teal-600 hover:bg-teal-700 text-white h-12 col-span-2"
                    data-testid="call-button"
                  >
                    <Phone size={18} className="mr-2" />
                    Ligar
                  </Button>
                ) : (
                  <Button
                    onClick={handleHangup}
                    className="bg-red-600 hover:bg-red-700 text-white h-12 col-span-2"
                    data-testid="hangup-button"
                  >
                    <PhoneOff size={18} className="mr-2" />
                    Desligar
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Seleção de Agenda */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Users size={18} className="text-teal-600" />
                Agenda de Contatos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs">Selecionar Lista</Label>
                <Select value={selectedList} onValueChange={setSelectedList} disabled={isCallActive || autoDialMode}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Escolha uma agenda" />
                  </SelectTrigger>
                  <SelectContent>
                    {contactLists.map((list) => (
                      <SelectItem key={list.id} value={list.id}>
                        {list.name} ({list.total_contacts})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  id="auto-dial"
                  checked={autoDialMode}
                  onCheckedChange={(checked) => {
                    if (!checked) {
                      handleStopAutoDial();
                    } else {
                      prepareAndStartCampaign();
                    }
                  }}
                  disabled={isCallActive || !selectedList}
                />
                <Label htmlFor="auto-dial" className="text-sm">
                  Discagem Automática
                </Label>
              </div>

              {autoDialMode && (
                <div className="flex items-center justify-between bg-teal-50 p-3 rounded-lg">
                  <div className="text-sm">
                    <p className="font-medium text-teal-900">Progresso</p>
                    <p className="text-teal-700">{currentContactIndex + 1} de {contacts.length}</p>
                  </div>
                  <Button
                    onClick={handleStopAutoDial}
                    size="sm"
                    variant="outline"
                    className="border-red-600 text-red-600 hover:bg-red-50"
                  >
                    <X size={14} className="mr-1" />
                    Parar
                  </Button>
                </div>
              )}

            </CardContent>
          </Card>
        </div>

        {/* COLUNA 2: Status e Controles de Chamada */}
        <div className="space-y-4">
          {/* Status da Chamada */}
          <Card className={isCallActive ? 'border-2 border-green-400' : ''}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Status da Chamada</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {isCallActive ? (
                <>
                  <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-green-900">Status:</span>
                      <div className="flex items-center gap-2">
                        <div className={`status-indicator ${callStatus === 'calling' ? 'status-calling' : 'status-online'}`}></div>
                        <span className="text-sm font-bold text-green-900">
                          {callStatus === 'calling'
                            ? (callDirection === 'inbound' ? 'Recebendo...' : 'Chamando...')
                            : 'Conectado'}
                        </span>
                      </div>
                    </div>
                    {callStatus === 'connected' && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-green-900">Duração:</span>
                        <span className="text-3xl font-bold text-green-600" data-testid="call-duration">
                          {formatTime(callDuration)}
                        </span>
                      </div>
                    )}
                  </div>

                  {currentContact && (
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-xs font-medium text-slate-600 mb-1">Contato Atual:</p>
                      <p className="text-base font-semibold text-slate-900">{currentContact.name}</p>
                      <p className="text-sm text-slate-700">{currentContact.phone}</p>
                    </div>
                  )}

                  {currentBina && (
                    <div className="text-sm">
                      <span className="text-slate-600">Ramal: </span>
                      <span className="font-mono font-medium">{currentBina}</span>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Phone size={40} className="mx-auto mb-2 opacity-50" />
                  <p>Nenhuma chamada ativa</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Controles de Chamada */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Controles de Chamada</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={toggleMute}
                  disabled={!isCallActive || callStatus !== 'connected'}
                  variant="outline"
                  className="h-14 flex-col"
                  data-testid="mute-button"
                >
                  {isMuted ? <MicOff size={20} /> : <Mic size={20} />}
                  <span className="text-xs mt-1">{isMuted ? 'Desmutar' : 'Mudo'}</span>
                </Button>

                <Button
                  onClick={toggleHold}
                  disabled={!isCallActive || callStatus !== 'connected'}
                  variant="outline"
                  className="h-14 flex-col"
                  data-testid="hold-button"
                >
                  {isOnHold ? <Play size={20} /> : <Pause size={20} />}
                  <span className="text-xs mt-1">{isOnHold ? 'Retomar' : 'Espera'}</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Display DTMF Recebido do Cliente */}
          <Card className="border-2 border-cyan-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Hash size={18} className="text-cyan-600" />
                  DTMF Recebido (Cliente)
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setClientDTMF('')}
                >
                  Limpar
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {clientDTMF ? (
                <div className="bg-cyan-50 p-4 rounded-lg">
                  <p className="text-2xl font-mono font-bold text-cyan-900 text-center">
                    {clientDTMF}
                  </p>
                </div>
              ) : (
                <p className="text-center text-slate-500 py-4 text-sm">
                  Aguardando cliente digitar...
                </p>
              )}
            </CardContent>
          </Card>

          {/* Contact Card - Only show when calling from contact list */}
          {isCallActive && currentContact && currentContact.list_id && (
            <Card className="border-2 border-purple-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <User size={18} className="text-purple-600" />
                  Informações do Contato
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Header Section */}
                  <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-slate-900 mb-1">
                          {currentContact.NomeRazao || currentContact.name}
                        </h3>
                        {currentContact.CPFCNPJ && (
                          <p className="text-xs text-slate-600 font-mono">
                            CPF/CNPJ: {currentContact.CPFCNPJ}
                          </p>
                        )}
                      </div>
                      <div className="bg-purple-100 rounded-full p-2">
                        <User size={20} className="text-purple-600" />
                      </div>
                    </div>
                  </div>

                  {/* Contact Information Grid */}
                  <div className="grid grid-cols-1 gap-3">
                    {/* Phone Numbers */}
                    {(currentContact.CEL1 || currentContact.CEL2 || currentContact.CEL3 || currentContact.FIXO1) && (
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Phone size={14} className="text-slate-600" />
                          <span className="text-xs font-semibold text-slate-700 uppercase">Telefones</span>
                        </div>
                        <div className="space-y-1 text-sm">
                          {currentContact.CEL1 && (
                            <div className="flex justify-between">
                              <span className="text-slate-600">Celular 1:</span>
                              <span className="font-mono font-medium text-slate-900">{currentContact.CEL1}</span>
                            </div>
                          )}
                          {currentContact.CEL2 && (
                            <div className="flex justify-between">
                              <span className="text-slate-600">Celular 2:</span>
                              <span className="font-mono font-medium text-slate-900">{currentContact.CEL2}</span>
                            </div>
                          )}
                          {currentContact.CEL3 && (
                            <div className="flex justify-between">
                              <span className="text-slate-600">Celular 3:</span>
                              <span className="font-mono font-medium text-slate-900">{currentContact.CEL3}</span>
                            </div>
                          )}
                          {currentContact.FIXO1 && (
                            <div className="flex justify-between">
                              <span className="text-slate-600">Fixo:</span>
                              <span className="font-mono font-medium text-slate-900">{currentContact.FIXO1}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Email Addresses */}
                    {(currentContact.Email1 || currentContact.Email2 || currentContact.Email3) && (
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Mail size={14} className="text-slate-600" />
                          <span className="text-xs font-semibold text-slate-700 uppercase">E-mails</span>
                        </div>
                        <div className="space-y-1 text-sm">
                          {currentContact.Email1 && (
                            <div className="text-slate-900 break-all">{currentContact.Email1}</div>
                          )}
                          {currentContact.Email2 && (
                            <div className="text-slate-900 break-all">{currentContact.Email2}</div>
                          )}
                          {currentContact.Email3 && (
                            <div className="text-slate-900 break-all">{currentContact.Email3}</div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Address Information */}
                    {(currentContact.EndLogradouro || currentContact.EndCidade || currentContact.EndUF || currentContact.EndCEP) && (
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                        <div className="flex items-center gap-2 mb-2">
                          <MapPin size={14} className="text-slate-600" />
                          <span className="text-xs font-semibold text-slate-700 uppercase">Endereço</span>
                        </div>
                        <div className="space-y-1 text-sm text-slate-900">
                          {currentContact.EndLogradouro && (
                            <div>
                              {currentContact.EndTipo && <span className="font-medium">{currentContact.EndTipo} </span>}
                              {currentContact.EndLogradouro}
                              {currentContact.EndNumero && `, ${currentContact.EndNumero}`}
                              {currentContact.EndComplemento && ` - ${currentContact.EndComplemento}`}
                            </div>
                          )}
                          {currentContact.EndBairro && (
                            <div className="text-slate-700">{currentContact.EndBairro}</div>
                          )}
                          {(currentContact.EndCidade || currentContact.EndUF) && (
                            <div>
                              {currentContact.EndCidade && <span>{currentContact.EndCidade}</span>}
                              {currentContact.EndUF && <span>{currentContact.EndCidade ? ' - ' : ''}{currentContact.EndUF}</span>}
                              {currentContact.EndCEP && <span className="ml-2 font-mono">CEP: {currentContact.EndCEP}</span>}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Personal Information */}
                    {(currentContact.DataNascAbertura || currentContact.Idade || currentContact.SexoMatriz || currentContact.EstadoCivil) && (
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Calendar size={14} className="text-slate-600" />
                          <span className="text-xs font-semibold text-slate-700 uppercase">Informações Pessoais</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {currentContact.DataNascAbertura && (
                            <div>
                              <span className="text-slate-600">Nasc.:</span>
                              <span className="ml-1 font-medium text-slate-900">{currentContact.DataNascAbertura}</span>
                            </div>
                          )}
                          {currentContact.Idade && (
                            <div>
                              <span className="text-slate-600">Idade:</span>
                              <span className="ml-1 font-medium text-slate-900">{currentContact.Idade}</span>
                            </div>
                          )}
                          {currentContact.SexoMatriz && (
                            <div>
                              <span className="text-slate-600">Sexo:</span>
                              <span className="ml-1 font-medium text-slate-900">{currentContact.SexoMatriz}</span>
                            </div>
                          )}
                          {currentContact.EstadoCivil && (
                            <div>
                              <span className="text-slate-600">Estado Civil:</span>
                              <span className="ml-1 font-medium text-slate-900">{currentContact.EstadoCivil}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Professional/Financial Information */}
                    {(currentContact.DescricaoCBOCNAE || currentContact.RendaFaturamento || currentContact.Escolaridade) && (
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Building size={14} className="text-slate-600" />
                          <span className="text-xs font-semibold text-slate-700 uppercase">Profissional/Financeiro</span>
                        </div>
                        <div className="space-y-1 text-sm">
                          {currentContact.DescricaoCBOCNAE && (
                            <div>
                              <span className="text-slate-600">Ocupação:</span>
                              <span className="ml-1 text-slate-900">{currentContact.DescricaoCBOCNAE}</span>
                            </div>
                          )}
                          {currentContact.RendaFaturamento && (
                            <div>
                              <span className="text-slate-600">Renda/Faturamento:</span>
                              <span className="ml-1 font-medium text-slate-900">{currentContact.RendaFaturamento}</span>
                            </div>
                          )}
                          {currentContact.Escolaridade && (
                            <div className="flex items-center gap-1">
                              <GraduationCap size={14} className="text-slate-600" />
                              <span className="text-slate-600">Escolaridade:</span>
                              <span className="ml-1 text-slate-900">{currentContact.Escolaridade}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Banking Information */}
                    {(currentContact.AGENCIA || currentContact.NRCONTACORRENTE || currentContact.CDBANCO) && (
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                        <div className="flex items-center gap-2 mb-2">
                          <CreditCard size={14} className="text-slate-600" />
                          <span className="text-xs font-semibold text-slate-700 uppercase">Dados Bancários</span>
                        </div>
                        <div className="space-y-1 text-sm">
                          {currentContact.CDBANCO && (
                            <div>
                              <span className="text-slate-600">Banco:</span>
                              <span className="ml-1 font-medium text-slate-900">{currentContact.CDBANCO}</span>
                            </div>
                          )}
                          {currentContact.AGENCIA && (
                            <div>
                              <span className="text-slate-600">Agência:</span>
                              <span className="ml-1 font-mono font-medium text-slate-900">{currentContact.AGENCIA}</span>
                            </div>
                          )}
                          {currentContact.NRCONTACORRENTE && (
                            <div>
                              <span className="text-slate-600">Conta:</span>
                              <span className="ml-1 font-mono font-medium text-slate-900">{currentContact.NRCONTACORRENTE}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* COLUNA 3: Áudio, Transferência e SMS */}
        <div className="space-y-4">
          {/* Reproduzir Áudio */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Music size={18} className="text-teal-600" />
                Reproduzir Áudio
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs">Selecionar Áudio</Label>
                <Select
                  value={selectedAudio}
                  onValueChange={setSelectedAudio}
                  disabled={!isCallActive || callStatus !== 'connected'}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Escolha um áudio" />
                  </SelectTrigger>
                  <SelectContent>
                    {audioFiles.map((audio) => (
                      <SelectItem key={audio.id} value={audio.id}>
                        {audio.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={isAudioPlaying ? handleStopAudio : handlePlayAudio}
                  disabled={!selectedAudio || !isCallActive || callStatus !== 'connected'}
                  className="flex-1"
                  variant={isAudioPlaying ? 'destructive' : 'default'}
                >
                  {isAudioPlaying ? (
                    <>
                      <Square size={16} className="mr-2" />
                      Parar Reprodução
                    </>
                  ) : (
                    <>
                      <Volume2 size={16} className="mr-2" />
                      Reproduzir para Cliente
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Transferir Chamada */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <PhoneForwarded size={18} className="text-teal-600" />
                Transferir Chamada
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs">Número de destino</Label>
                <Input
                  type="tel"
                  value={transferNumber}
                  onChange={(e) => setTransferNumber(e.target.value)}
                  placeholder="Digite o número"
                  className="h-9"
                  disabled={!isCallActive || callStatus !== 'connected'}
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Transferência para ramais (5 dígitos) não é permitida
                </p>
              </div>
              <Button
                onClick={handleTransfer}
                disabled={!transferNumber || !isCallActive || callStatus !== 'connected'}
                className="w-full"
              >
                <PhoneForwarded size={16} className="mr-2" />
                Transferir Agora
              </Button>
            </CardContent>
          </Card>

          {/* Enviar SMS - Hidden for now, will be implemented later */}
          {false && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <MessageSquare size={18} className="text-teal-600" />
                  Enviar SMS
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-xs">Mensagem</Label>
                  <Textarea
                    value={smsMessage}
                    onChange={(e) => setSmsMessage(e.target.value)}
                    placeholder="Digite a mensagem..."
                    rows={4}
                    className="resize-none"
                  />
                </div>
                <Button
                  onClick={handleSendSMS}
                  disabled={!smsMessage}
                  className="w-full"
                >
                  <MessageSquare size={16} className="mr-2" />
                  Enviar SMS
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Contatos Pendentes */}
          {selectedList && contacts.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">
                  Contatos pendentes ({contacts.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {contacts.slice(0, 10).map((contact, index) => (
                    <div
                      key={contact.id}
                      onClick={() => !autoDialMode && !isCallActive && handleContactClick(contact, index)}
                      className={`p-2 rounded border text-sm transition-colors ${
                        (autoDialMode && index === currentContactIndex) || 
                        (!autoDialMode && index === selectedContactIndex)
                          ? 'border-teal-600 bg-teal-50'
                          : 'border-slate-200 bg-slate-50'
                      } ${
                        autoDialMode || isCallActive 
                          ? 'cursor-not-allowed opacity-60' 
                          : 'cursor-pointer hover:bg-slate-100'
                      }`}
                    >
                      <p className="font-medium text-slate-800">{contact.name}</p>
                      <p className="text-xs text-slate-600">{contact.phone}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Countdown Overlay */}
          {showCountdown && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <Card className="w-96">
                <CardHeader>
                  <CardTitle className="text-center">Discagem Automática</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center">
                    <div className="text-6xl font-bold text-teal-600 mb-4">
                      {countdown}
                    </div>
                    <p className="text-slate-600 mb-4">
                      Iniciando discagem automática em...
                    </p>
                  </div>
                  <Button
                    onClick={handleStopAutoDial}
                    variant="destructive"
                    className="w-full"
                  >
                    <X size={16} className="mr-2" />
                    Parar Discagem Automática
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CallPanel;
